/**
 * Same-origin fetch wrapper for the /api/* endpoints.
 *
 * - Sends the session cookie via `credentials: 'same-origin'`.
 * - Reads the CSRF token from the `XSRF-TOKEN` cookie set by the server on
 *   first hit, and echoes it back in the `X-XSRF-TOKEN` header on POST/PUT/
 *   PATCH/DELETE requests.
 * - Parses JSON responses. Throws an `ApiError` with `.code`, `.message`,
 *   and `.status` on non-2xx.
 */

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const CSRF_COOKIE = 'XSRF-TOKEN';
const CSRF_HEADER = 'X-XSRF-TOKEN';

export class ApiError extends Error {
  constructor(code, message, status, data) {
    super(message || code || 'API error');
    this.name = 'ApiError';
    this.code = code || 'unknown_error';
    this.status = status || 0;
    this.data = data || null;
  }
}

function readCookie(name) {
  const prefix = name + '=';
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (let i = 0; i < parts.length; i++) {
    if (parts[i].indexOf(prefix) === 0) {
      return decodeURIComponent(parts[i].slice(prefix.length));
    }
  }
  return '';
}

/**
 * Low-level API call.
 *
 * @param {string} path — leading slash required, e.g. `/api/auth/me`.
 * @param {object} opts — { method, body, headers, signal }.
 * @returns {Promise<any>} parsed JSON body.
 */
export async function apiFetch(path, opts = {}) {
  const method = (opts.method || 'GET').toUpperCase();
  const headers = {
    Accept: 'application/json',
    ...(opts.headers || {}),
  };

  let body = opts.body;
  if (body && typeof body === 'object' && !(body instanceof FormData)) {
    body = JSON.stringify(body);
    if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';
  }

  if (MUTATING_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE);
    if (token) headers[CSRF_HEADER] = token;
  }

  let response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      body,
      headers,
      signal: opts.signal,
    });
  } catch (e) {
    throw new ApiError('network_error', e.message || 'Network error', 0);
  }

  const text = await response.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      /* non-JSON response is fine for some endpoints */
    }
  }

  if (!response.ok) {
    const code = (data && data.error) || 'http_' + response.status;
    const message = (data && data.message) || response.statusText || 'Error';
    throw new ApiError(code, message, response.status, data);
  }

  return data;
}

/** Convenience wrappers. */
export const api = {
  get: (path, opts) => apiFetch(path, { ...(opts || {}), method: 'GET' }),
  post: (path, body, opts) =>
    apiFetch(path, { ...(opts || {}), method: 'POST', body }),
  put: (path, body, opts) =>
    apiFetch(path, { ...(opts || {}), method: 'PUT', body }),
  patch: (path, body, opts) =>
    apiFetch(path, { ...(opts || {}), method: 'PATCH', body }),
  del: (path, opts) => apiFetch(path, { ...(opts || {}), method: 'DELETE' }),
};
